import React, { useState, useMemo } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { useTransactions } from '../contexts/TransactionContext';
import { useAccounts } from '../contexts/AccountContext';
import { useAuth } from '../contexts/AuthContext';
import {
  generateTransactionsCSV,
  generatePrintableStatementHTML,
  calculateStatementSummary,
  formatTxDate,
} from 'shared/utils/exportUtils';
import {
  X,
  Download,
  FileText,
  Printer,
  Calendar,
  Wallet,
  Filter,
  CheckCircle2,
  Copy,
  Layers,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  startOfMonth,
  endOfMonth,
  subMonths,
  startOfYear,
  isWithinInterval,
} from 'date-fns';

const DATE_PRESETS = [
  { id: 'this_month', label: 'This Month' },
  { id: 'last_month', label: 'Last Month' },
  { id: 'last_3_months', label: 'Last 3 Months' },
  { id: 'ytd', label: 'Year to Date' },
  { id: 'all_time', label: 'All Time' },
  { id: 'custom', label: 'Custom Range' },
];

export default function ExportReportModal({ isOpen, onClose }) {
  const { theme, isDark } = useTheme();
  const { displayCurrency, getCurrencyInfo, formatCurrency, currency: ctxCurrency } = useCurrency();
  const currencyInfo = getCurrencyInfo ? getCurrencyInfo(displayCurrency) : null;
  const currencySymbol = currencyInfo?.symbol || ctxCurrency?.symbol || '$';
  const { transactions } = useTransactions();
  const { accounts } = useAccounts();
  const { currentUser } = useAuth();

  const [datePreset, setDatePreset] = useState('this_month');
  const [startDate, setStartDate] = useState(
    formatTxDate(startOfMonth(new Date()), 'yyyy-MM-dd')
  );
  const [endDate, setEndDate] = useState(
    formatTxDate(endOfMonth(new Date()), 'yyyy-MM-dd')
  );
  const [selectedAccount, setSelectedAccount] = useState('all');
  const [selectedType, setSelectedType] = useState('all'); // all | expense | income

  // Filter transactions based on active selection
  const filteredTransactions = useMemo(() => {
    let list = [...(transactions || [])];

    // 1. Account Filter
    if (selectedAccount !== 'all') {
      list = list.filter((t) => t.account === selectedAccount);
    }

    // 2. Type Filter
    if (selectedType !== 'all') {
      list = list.filter((t) => t.type === selectedType);
    }

    // 3. Date Filter
    const now = new Date();
    let interval = null;

    if (datePreset === 'this_month') {
      interval = { start: startOfMonth(now), end: endOfMonth(now) };
    } else if (datePreset === 'last_month') {
      const prev = subMonths(now, 1);
      interval = { start: startOfMonth(prev), end: endOfMonth(prev) };
    } else if (datePreset === 'last_3_months') {
      interval = { start: startOfMonth(subMonths(now, 2)), end: endOfMonth(now) };
    } else if (datePreset === 'ytd') {
      interval = { start: startOfYear(now), end: endOfMonth(now) };
    } else if (datePreset === 'custom') {
      if (startDate && endDate) {
        interval = {
          start: new Date(`${startDate}T00:00:00`),
          end: new Date(`${endDate}T23:59:59`),
        };
      }
    }

    if (interval) {
      list = list.filter((t) => {
        const d = t.date instanceof Date ? t.date : new Date(t.date);
        return isWithinInterval(d, interval);
      });
    }

    // Sort chronologically desc
    return list.sort((a, b) => {
      const dateA = a.date instanceof Date ? a.date : new Date(a.date);
      const dateB = b.date instanceof Date ? b.date : new Date(b.date);
      return dateB - dateA;
    });
  }, [transactions, selectedAccount, selectedType, datePreset, startDate, endDate]);

  const summary = useMemo(() => {
    return calculateStatementSummary(filteredTransactions, currencySymbol);
  }, [filteredTransactions, currencySymbol]);

  const dateRangeLabel = useMemo(() => {
    if (datePreset === 'custom') {
      return `${startDate} to ${endDate}`;
    }
    const preset = DATE_PRESETS.find((p) => p.id === datePreset);
    return preset ? preset.label : 'Selected Range';
  }, [datePreset, startDate, endDate]);

  // Handle CSV Download
  const handleDownloadCSV = () => {
    if (filteredTransactions.length === 0) {
      toast.error('No transactions match the selected filters');
      return;
    }

    const csvContent = generateTransactionsCSV(filteredTransactions, {
      currencySymbol,
      dateRangeLabel,
    });

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const filename = `SpendEasy_Export_${datePreset}_${formatTxDate(new Date(), 'yyyyMMdd')}.csv`;

    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast.success(`Downloaded ${filteredTransactions.length} transactions as CSV`);
  };

  // Copy CSV to Clipboard
  const handleCopyCSV = () => {
    if (filteredTransactions.length === 0) {
      toast.error('No transactions to copy');
      return;
    }
    const csvContent = generateTransactionsCSV(filteredTransactions, {
      currencySymbol,
      dateRangeLabel,
    });
    navigator.clipboard.writeText(csvContent);
    toast.success('Copied CSV to clipboard!');
  };

  // Handle PDF Statement Printing / Download
  const handlePrintPDF = () => {
    if (filteredTransactions.length === 0) {
      toast.error('No transactions match the selected filters');
      return;
    }

    const html = generatePrintableStatementHTML(filteredTransactions, {
      title: `Spend Easy Statement - ${dateRangeLabel}`,
      dateRangeLabel,
      currencySymbol,
      accountName:
        selectedAccount === 'all'
          ? 'All Accounts'
          : accounts.find((a) => a.id === selectedAccount)?.name || selectedAccount,
      userName: currentUser?.displayName || currentUser?.email || 'User Account',
    });

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error('Please allow popups to open and print the PDF statement.');
      return;
    }

    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();

    // Trigger print after styles load
    setTimeout(() => {
      printWindow.focus();
      printWindow.print();
    }, 500);
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl max-h-[92vh] overflow-y-auto rounded-2xl p-6 shadow-2xl transition-all"
        style={{
          backgroundColor: theme.cardBackground,
          border: `1px solid ${theme.divider}`,
          color: theme.text,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between pb-4 border-b" style={{ borderColor: theme.divider }}>
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: `${theme.accent}15`, color: theme.accent }}
            >
              <Download className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-xl font-bold">Export Financial Reports</h2>
              <p className="text-xs" style={{ color: theme.textSecondary }}>
                Download clean CSV spreadsheets or formatted PDF statements
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg transition-colors hover:opacity-75"
            style={{ color: theme.textSecondary }}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Date Presets */}
        <div className="mt-5">
          <label className="text-xs font-bold uppercase tracking-wider block mb-2" style={{ color: theme.textSecondary }}>
            1. Select Statement Period
          </label>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
            {DATE_PRESETS.map((p) => {
              const active = datePreset === p.id;
              return (
                <button
                  key={p.id}
                  onClick={() => setDatePreset(p.id)}
                  className="py-2 px-3 rounded-lg text-xs font-semibold transition-all text-center"
                  style={{
                    backgroundColor: active ? theme.accent : isDark ? theme.inputBackground : '#F3F4F6',
                    color: active ? '#FFFFFF' : theme.text,
                    border: `1px solid ${active ? theme.accent : theme.divider}`,
                  }}
                >
                  {p.label}
                </button>
              );
            })}
          </div>

          {/* Custom Date Inputs */}
          {datePreset === 'custom' && (
            <div className="grid grid-cols-2 gap-3 mt-3 p-3 rounded-xl border" style={{ borderColor: theme.divider }}>
              <div>
                <label className="text-xs block mb-1 font-medium" style={{ color: theme.textSecondary }}>
                  Start Date
                </label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg text-xs border"
                  style={{
                    backgroundColor: isDark ? theme.inputBackground : '#FFFFFF',
                    borderColor: theme.divider,
                    color: theme.text,
                  }}
                />
              </div>
              <div>
                <label className="text-xs block mb-1 font-medium" style={{ color: theme.textSecondary }}>
                  End Date
                </label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg text-xs border"
                  style={{
                    backgroundColor: isDark ? theme.inputBackground : '#FFFFFF',
                    borderColor: theme.divider,
                    color: theme.text,
                  }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Filters Row (Account & Type) */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
          <div>
            <label className="text-xs font-bold uppercase tracking-wider block mb-2" style={{ color: theme.textSecondary }}>
              2. Filter Account
            </label>
            <select
              value={selectedAccount}
              onChange={(e) => setSelectedAccount(e.target.value)}
              className="w-full px-3 py-2.5 rounded-xl text-xs font-medium border"
              style={{
                backgroundColor: isDark ? theme.inputBackground : '#FFFFFF',
                borderColor: theme.divider,
                color: theme.text,
              }}
            >
              <option value="all">All Accounts (Consolidated)</option>
              {accounts.map((a) => (
                <option key={a.id || a.name} value={a.name}>
                  {a.name} ({a.type || 'Account'})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-xs font-bold uppercase tracking-wider block mb-2" style={{ color: theme.textSecondary }}>
              3. Transaction Type
            </label>
            <div className="flex gap-2">
              {[
                { id: 'all', label: 'All' },
                { id: 'expense', label: 'Expenses' },
                { id: 'income', label: 'Income' },
              ].map((t) => {
                const active = selectedType === t.id;
                return (
                  <button
                    key={t.id}
                    onClick={() => setSelectedType(t.id)}
                    className="flex-1 py-2.5 px-2 rounded-xl text-xs font-semibold border transition-all text-center"
                    style={{
                      backgroundColor: active ? theme.accent : isDark ? theme.inputBackground : '#F3F4F6',
                      color: active ? '#FFFFFF' : theme.text,
                      borderColor: active ? theme.accent : theme.divider,
                    }}
                  >
                    {t.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Live Filter Summary Card */}
        <div
          className="mt-5 p-4 rounded-xl border flex flex-wrap items-center justify-between gap-4"
          style={{
            backgroundColor: isDark ? '#1C2331' : '#F8FAFC',
            borderColor: theme.divider,
          }}
        >
          <div>
            <div className="text-xs font-medium" style={{ color: theme.textSecondary }}>
              Report Scope: <strong style={{ color: theme.text }}>{dateRangeLabel}</strong>
            </div>
            <div className="text-lg font-bold mt-0.5">
              {filteredTransactions.length} Transactions Found
            </div>
          </div>

          <div className="flex items-center gap-4 text-xs font-semibold">
            <div>
              <span className="text-gray-400 block text-[10px]">TOTAL INCOME</span>
              <span className="text-green-500 font-bold">+{formatCurrency(summary.totalIncome)}</span>
            </div>
            <div className="h-6 w-px bg-gray-300 dark:bg-gray-700" />
            <div>
              <span className="text-gray-400 block text-[10px]">TOTAL EXPENSE</span>
              <span className="text-red-500 font-bold">-{formatCurrency(summary.totalExpense)}</span>
            </div>
            <div className="h-6 w-px bg-gray-300 dark:bg-gray-700" />
            <div>
              <span className="text-gray-400 block text-[10px]">NET BALANCE</span>
              <span className="font-bold">{formatCurrency(summary.netSavings)}</span>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-6">
          {/* CSV Export Button */}
          <div
            className="p-4 rounded-xl border flex flex-col justify-between"
            style={{
              backgroundColor: isDark ? theme.inputBackground : '#FFFFFF',
              borderColor: theme.divider,
            }}
          >
            <div>
              <div className="flex items-center gap-2 mb-1.5">
                <FileText className="w-5 h-5 text-emerald-500" />
                <h4 className="font-bold text-sm">Spreadsheet CSV</h4>
              </div>
              <p className="text-xs mb-3" style={{ color: theme.textSecondary }}>
                Formatted for Excel, Google Sheets, Numbers, Quicken, and tax filers.
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleDownloadCSV}
                className="flex-1 flex items-center justify-center gap-2 py-2.5 px-3 rounded-lg text-xs font-bold text-white transition-all shadow hover:opacity-90 active:scale-98"
                style={{ backgroundColor: '#10B981' }}
              >
                <Download className="w-3.5 h-3.5" />
                Download CSV
              </button>
              <button
                onClick={handleCopyCSV}
                className="p-2.5 rounded-lg border text-xs font-medium transition-all hover:opacity-75"
                style={{ borderColor: theme.divider, color: theme.text }}
                title="Copy CSV to clipboard"
              >
                <Copy className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* PDF Statement Button */}
          <div
            className="p-4 rounded-xl border flex flex-col justify-between"
            style={{
              backgroundColor: isDark ? theme.inputBackground : '#FFFFFF',
              borderColor: theme.divider,
            }}
          >
            <div>
              <div className="flex items-center gap-2 mb-1.5">
                <Printer className="w-5 h-5" style={{ color: theme.accent }} />
                <h4 className="font-bold text-sm">PDF Statement</h4>
              </div>
              <p className="text-xs mb-3" style={{ color: theme.textSecondary }}>
                Print-ready official document with KPI cards, category breakdown & logs.
              </p>
            </div>
            <button
              onClick={handlePrintPDF}
              className="w-full flex items-center justify-center gap-2 py-2.5 px-3 rounded-lg text-xs font-bold text-white transition-all shadow hover:opacity-90 active:scale-98"
              style={{ backgroundColor: theme.accent }}
            >
              <Printer className="w-3.5 h-3.5" />
              Generate PDF Statement
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
